import { useEffect, useState } from 'react';
import { apiRequest, invalidateApiCache } from '../../lib/api';
import { uploadFiles, API_BASE } from '../../lib/api';
import { FormSkeletonLoader } from '../SkeletonLoader';

export type BlockType = 'Hero' | 'SplitBanner' | 'MiniCards' | 'ProductsList' | 'Features' | 'Stats' | 'LatestNews' | 'CallToAction';

export interface BaseBlock {
  id: string;
  type: BlockType;
}

export interface HeroBlock extends BaseBlock {
  type: 'Hero';
  eyebrow: string;
  title: string;
  description: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  secondaryButtonText: string;
  secondaryButtonLink: string;
}

export interface SplitBannerBlock extends BaseBlock {
  type: 'SplitBanner';
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  secondaryButtonText: string;
  secondaryButtonLink: string;
  imagePosition: 'left' | 'right';
}

export interface MiniCardsBlock extends BaseBlock {
  type: 'MiniCards';
  cards: { label: string; labelColor: string; title: string; description: string }[];
}

export interface ProductsListBlock extends BaseBlock {
  type: 'ProductsList';
  eyebrow: string;
  title: string;
  limit: number;
}

export interface FeaturesBlock extends BaseBlock {
  type: 'Features';
  eyebrow?: string;
  title: string;
  items: { icon: string; title: string; description: string }[];
}

export interface LatestNewsBlock extends BaseBlock {
  type: 'LatestNews';
  eyebrow?: string;
  title: string;
  limit: number;
}

export interface CallToActionBlock extends BaseBlock {
  type: 'CallToAction';
  title: string;
  buttonText: string;
  buttonLink: string;
}

export interface StatsBlock extends BaseBlock {
  type: 'Stats';
  items: { label: string; value: string }[];
}

export type HomeBlock = HeroBlock | SplitBannerBlock | MiniCardsBlock | ProductsListBlock | FeaturesBlock | StatsBlock | LatestNewsBlock | CallToActionBlock;

export const DEFAULT_HOME_LAYOUT: HomeBlock[] = [
  {
    id: 'hero-1',
    type: 'Hero',
    eyebrow: '3D SMART - H&L STORE',
    title: 'Giải pháp 3D & Công nghệ thông minh cho cuộc sống hiện đại',
    description: 'Khám phá các sản phẩm in 3D, BMCU, phụ kiện máy in Bambu Lab và những giải pháp công nghệ được thiết kế với tiêu chí thực tế, chính xác và tiện dụng. 3D SMART hướng đến việc kết hợp giữa sáng tạo 3D, điện tử và IoT để tạo ra những sản phẩm hữu ích cho cuộc sống.',
    primaryButtonText: 'Khám phá sản phẩm',
    primaryButtonLink: '/products',
    secondaryButtonText: 'Về 3D SMART',
    secondaryButtonLink: '/about'
  },
  {
    id: 'minicards-1',
    type: 'MiniCards',
    cards: [
      { label: '3D PRINTING', labelColor: '#ef4444', title: 'Sản phẩm in 3D', description: 'Đa dạng sản phẩm in 3D từ phụ kiện, đồ dùng đến các mẫu thiết kế độc đáo cho không gian sống.' },
      { label: 'BMCU', labelColor: '#94a3b8', title: 'BMCU 370X Type C', description: 'Giải pháp thay thế AMS Bambu Lab, hỗ trợ in 3D nhiều màu tự động.' },
      { label: 'BAMBU LAB', labelColor: '#f1f5f9', title: 'Phụ kiện máy in', description: 'Phụ kiện và linh kiện tương thích Bambu Lab, hỗ trợ nâng cấp và sử dụng máy in hiệu quả hơn.' }
    ]
  },
  {
    id: 'splitbanner-1',
    type: 'SplitBanner',
    eyebrow: 'Sản phẩm nổi bật',
    title: 'Từ mã nguồn mở đến giải pháp BMCU hoàn thiện',
    description: 'BMCU là nền tảng mã nguồn mở được sử dụng rộng rãi trong cộng đồng in 3D. 3D SMART phát triển firmware tùy biến, tối ưu trải nghiệm sử dụng và bổ sung hệ thống kích hoạt bảo hành online, cùng khả năng nạp và cập nhật firmware nhanh chóng trực tiếp trên website.',
    image: '/uploads/1787845878496-731849980.jpg',
    primaryButtonText: 'Mua ngay',
    primaryButtonLink: 'https://www.youtube.com/watch?v=UtEHmM8F4ew',
    secondaryButtonText: 'Khám phá thêm',
    secondaryButtonLink: '/products',
    imagePosition: 'left'
  },
  {
    id: 'products-1',
    type: 'ProductsList',
    eyebrow: 'Sản phẩm nổi bật',
    title: 'Danh sách sản phẩm',
    limit: 3
  },
  {
    id: 'features-1',
    type: 'Features',
    eyebrow: 'Giá trị của 3D SMART',
    title: 'Sáng tạo với công nghệ, chú trọng từng chi tiết',
    items: [
      { icon: '01', title: 'Thiết kế thực tế', description: 'Tập trung vào tính ứng dụng, thẩm mỹ và khả năng sử dụng lâu dài.' },
      { icon: '02', title: 'Chất lượng in 3D', description: 'Ưu tiên độ chính xác, độ hoàn thiện và lựa chọn vật liệu phù hợp cho từng sản phẩm.' },
      { icon: '03', title: 'Nhiều giải pháp', description: 'Từ sản phẩm in 3D, BMCU đến phụ kiện máy in và các sản phẩm công nghệ.' }
    ]
  },
  {
    id: 'latestnews-1',
    type: 'LatestNews',
    eyebrow: 'Tin tức',
    title: 'Cập nhật mới nhất',
    limit: 3
  },
  {
    id: 'calltoaction-1',
    type: 'CallToAction',
    title: 'Khám phá hệ sinh thái 3D SMART',
    buttonText: 'Xem sản phẩm',
    buttonLink: '/products'
  }
];

export default function HomeBuilderTab({ showToast }: { showToast: (msg: string, type?: 'info'|'error'|'warning') => void }) {
  const [blocks, setBlocks] = useState<HomeBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null);
  const [postId, setPostId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await apiRequest<{ posts: any[] }>('/posts');
        const layoutPost = res.posts.find(p => p.slug === 'home-layout');
        if (layoutPost) {
          setPostId(layoutPost.id);
          try {
            setBlocks(JSON.parse(layoutPost.content));
          } catch {
            setBlocks(DEFAULT_HOME_LAYOUT);
          }
        } else {
          setBlocks(DEFAULT_HOME_LAYOUT);
        }
      } catch (e) {
        showToast('Lỗi tải cấu hình trang chủ', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        title: 'Cấu hình Trang chủ',
        slug: 'home-layout',
        excerpt: 'Dynamic blocks cho trang chủ',
        content: JSON.stringify(blocks),
        category: 'System'
      };
      
      if (postId) {
        await apiRequest(`/posts/${postId}`, { method: 'PUT', body: JSON.stringify(payload) }, true);
      } else {
        const res = await apiRequest<{ post: any }>('/posts', { method: 'POST', body: JSON.stringify(payload) }, true);
        setPostId(res.post.id);
      }
      invalidateApiCache('/posts');
      showToast('Đã lưu cấu hình trang chủ', 'info');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lỗi lưu dữ liệu', 'error');
    } finally {
      setSaving(false);
    }
  }

  function moveUp(index: number) {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    setBlocks(newBlocks);
  }

  function moveDown(index: number) {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index + 1], newBlocks[index]] = [newBlocks[index], newBlocks[index + 1]];
    setBlocks(newBlocks);
  }

  function removeBlock(index: number) {
    setConfirmDeleteIdx(index);
  }

  function confirmRemoveBlock() {
    if (confirmDeleteIdx === null) return;
    setBlocks(prev => {
      const newBlocks = [...prev];
      newBlocks.splice(confirmDeleteIdx, 1);
      return newBlocks;
    });
    setConfirmDeleteIdx(null);
  }

  const handleImageUpload = async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
        const file = input.files ? input.files[0] : null;
        if (!file) {
          resolve(null);
          return;
        }
        try {
          showToast('Đang tải ảnh lên...', 'info');
          const uploadedUrls = await uploadFiles([file]);
          if (uploadedUrls && uploadedUrls.length > 0) {
            const fullUrl = API_BASE.replace('/api', '') + uploadedUrls[0];
            showToast('Tải ảnh thành công!', 'info');
            resolve(fullUrl);
          } else {
            resolve(null);
          }
        } catch (e) {
          showToast('Tải ảnh thất bại', 'error');
          resolve(null);
        }
      };
    });
  };

  function addBlock(type: BlockType) {
    const id = type.toLowerCase() + '-' + Date.now();
    let newBlock: HomeBlock;
    
    switch (type) {
      case 'Hero':
        newBlock = { id, type, eyebrow: 'Eyebrow', title: 'Tiêu đề', description: 'Mô tả', primaryButtonText: 'Nút 1', primaryButtonLink: '#', secondaryButtonText: 'Nút 2', secondaryButtonLink: '#' };
        break;
      case 'SplitBanner':
        newBlock = { id, type, eyebrow: 'Tính năng mới', title: 'Tiêu đề Banner', description: 'Mô tả chi tiết', image: '/images/banners/Banner.png', primaryButtonText: 'Mua ngay', primaryButtonLink: '#', secondaryButtonText: '', secondaryButtonLink: '', imagePosition: 'right' };
        break;
      case 'MiniCards':
        newBlock = { id, type, cards: [{ label: 'Nhãn', labelColor: '#b80000', title: 'Tiêu đề', description: 'Mô tả' }] };
        break;
      case 'ProductsList':
        newBlock = { id, type, eyebrow: 'Eyebrow', title: 'Danh sách sản phẩm', limit: 4 };
        break;
      case 'Features':
        newBlock = { id, type, eyebrow: 'Eyebrow', title: 'Tính năng', items: [{ icon: '01', title: 'Tính năng 1', description: 'Mô tả' }] };
        break;
      case 'Stats':
        newBlock = { id, type, items: [{ label: 'Khách hàng', value: '500+' }, { label: 'Đơn hàng', value: '1000+' }] };
        break;
      case 'LatestNews':
        newBlock = { id, type, eyebrow: 'Tin tức', title: 'Cập nhật mới nhất', limit: 3 };
        break;
      case 'CallToAction':
        newBlock = { id, type, title: 'Bạn đã sẵn sàng?', buttonText: 'Mua ngay', buttonLink: '/products' };
        break;
      default: return;
    }
    setBlocks([...blocks, newBlock]);
  }

  const renderBlockEditor = (block: HomeBlock, index: number) => {
    const updateBlock = (newValues: Partial<HomeBlock>) => {
      setBlocks(prev => {
        const newBlocks = [...prev];
        newBlocks[index] = { ...newBlocks[index], ...newValues } as HomeBlock;
        return newBlocks;
      });
    }

    const inputStyle = { padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'rgba(0,0,0,0.2)', color: '#f1f5f9', width: '100%', marginBottom: '4px', fontSize: '14px', transition: 'border 0.2s', outline: 'none' };
    const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#cbd5e1', marginBottom: '6px' };
    const uploadBtnStyle = { padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.1)', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: '#f8fafc' };

    return (
      <div key={block.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', color: '#f8fafc' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '24px', background: 'var(--neon-red)', borderRadius: '4px', boxShadow: '0 0 8px var(--neon-red)' }}></span>
            {block.type}
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => moveUp(index)} disabled={index === 0} style={{ padding: '6px 12px', cursor: index === 0 ? 'not-allowed' : 'pointer', borderRadius: '6px', border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.1)', color: '#cbd5e1', opacity: index === 0 ? 0.5 : 1 }}>↑ Lên</button>
            <button onClick={() => moveDown(index)} disabled={index === blocks.length - 1} style={{ padding: '6px 12px', cursor: index === blocks.length - 1 ? 'not-allowed' : 'pointer', borderRadius: '6px', border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.1)', color: '#cbd5e1', opacity: index === blocks.length - 1 ? 0.5 : 1 }}>↓ Xuống</button>
            <button onClick={() => removeBlock(index)} style={{ padding: '6px 12px', background: 'rgba(239, 68, 68, 0.2)', color: '#f87171', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Xóa</button>
          </div>
        </div>

        {/* Dynamic Fields based on type */}
        {block.type === 'Hero' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Eyebrow (Chữ nhỏ phía trên)</label>
              <input style={inputStyle} value={block.eyebrow} onChange={e => updateBlock({ eyebrow: e.target.value })} placeholder="VD: 3D SMART - H&L STORE" />
            </div>
            <div>
              <label style={labelStyle}>Tiêu đề chính (Title)</label>
              <input style={inputStyle} value={block.title} onChange={e => updateBlock({ title: e.target.value })} placeholder="VD: Giải pháp 3D & Công nghệ thông minh" />
            </div>
            <div>
              <label style={labelStyle}>Mô tả (Description)</label>
              <textarea style={{...inputStyle, minHeight: '90px', resize: 'vertical'}} value={block.description} onChange={e => updateBlock({ description: e.target.value })} placeholder="Mô tả tóm tắt..." />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '6px' }}>
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--neon-red)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Nút bấm chính (Primary)
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={labelStyle}>Tên nút</label>
                  <input style={inputStyle} value={block.primaryButtonText} onChange={e => updateBlock({ primaryButtonText: e.target.value })} placeholder="VD: Khám phá sản phẩm" />
                </div>
                <div>
                  <label style={labelStyle}>Link nút</label>
                  <input style={inputStyle} value={block.primaryButtonLink} onChange={e => updateBlock({ primaryButtonLink: e.target.value })} placeholder="VD: /products" />
                </div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Nút bấm phụ (Secondary)
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={labelStyle}>Tên nút</label>
                  <input style={inputStyle} value={block.secondaryButtonText} onChange={e => updateBlock({ secondaryButtonText: e.target.value })} placeholder="VD: Về 3D SMART" />
                </div>
                <div>
                  <label style={labelStyle}>Link nút</label>
                  <input style={inputStyle} value={block.secondaryButtonLink} onChange={e => updateBlock({ secondaryButtonLink: e.target.value })} placeholder="VD: /about" />
                </div>
              </div>
            </div>
          </div>
        )}

        {block.type === 'SplitBanner' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Eyebrow (Chữ nhỏ phía trên)</label>
                <input style={inputStyle} value={block.eyebrow} onChange={e => updateBlock({ eyebrow: e.target.value })} placeholder="VD: Sản phẩm nổi bật" />
              </div>
              <div>
                <label style={labelStyle}>Vị trí ảnh</label>
                <select style={inputStyle} value={block.imagePosition} onChange={e => updateBlock({ imagePosition: e.target.value as 'left' | 'right' })}>
                  <option value="right">Ảnh bên Phải</option>
                  <option value="left">Ảnh bên Trái</option>
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Image URL (Ảnh minh họa)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input style={{...inputStyle, flex: 1}} value={block.image} onChange={e => updateBlock({ image: e.target.value })} />
                <button type="button" onClick={async () => { const url = await handleImageUpload(); if (url) updateBlock({ image: url }); }} style={uploadBtnStyle}>Tải ảnh lên</button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Tiêu đề chính (Title)</label>
              <input style={inputStyle} value={block.title} onChange={e => updateBlock({ title: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>Mô tả (Description)</label>
              <textarea style={{...inputStyle, minHeight: '90px', resize: 'vertical'}} value={block.description} onChange={e => updateBlock({ description: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '6px' }}>
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--neon-red)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Nút bấm chính
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={labelStyle}>Tên nút chính (Tùy chọn)</label>
                  <input style={inputStyle} value={block.primaryButtonText} onChange={e => updateBlock({ primaryButtonText: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Link nút chính</label>
                  <input style={inputStyle} value={block.primaryButtonLink} onChange={e => updateBlock({ primaryButtonLink: e.target.value })} />
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Nút bấm phụ
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={labelStyle}>Tên nút phụ (Tùy chọn)</label>
                  <input style={inputStyle} value={block.secondaryButtonText} onChange={e => updateBlock({ secondaryButtonText: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Link nút phụ</label>
                  <input style={inputStyle} value={block.secondaryButtonLink} onChange={e => updateBlock({ secondaryButtonLink: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
        )}

        {(block.type === 'ProductsList' || block.type === 'LatestNews') && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {'eyebrow' in block && <div><label style={labelStyle}>Eyebrow</label><input style={inputStyle} value={block.eyebrow} onChange={e => updateBlock({ eyebrow: e.target.value } as any)} /></div>}
            <div><label style={labelStyle}>Số lượng hiển thị</label><input type="number" style={inputStyle} value={block.limit} onChange={e => updateBlock({ limit: Number(e.target.value) })} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Tiêu đề (Title)</label><input style={inputStyle} value={block.title} onChange={e => updateBlock({ title: e.target.value })} /></div>
          </div>
        )}

        {(block.type === 'Features' || block.type === 'MiniCards') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {'eyebrow' in block && <div><label style={labelStyle}>Eyebrow</label><input style={inputStyle} value={block.eyebrow} onChange={e => updateBlock({ eyebrow: e.target.value } as any)} /></div>}
            {'title' in block && <div><label style={labelStyle}>Tiêu đề</label><input style={inputStyle} value={block.title} onChange={e => updateBlock({ title: e.target.value } as any)} /></div>}
            
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
              <strong style={{ display: 'block', marginBottom: '12px', color: '#cbd5e1' }}>Các mục:</strong>
              {((block as any).items || (block as any).cards || []).map((item: any, i: number) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  {block.type === 'MiniCards' && <input style={{...inputStyle, width: '100px'}} placeholder="Label" value={item.label} onChange={e => { const items = [...((block as any).cards)]; items[i].label = e.target.value; updateBlock({ cards: items }); }} />}
                  {block.type === 'MiniCards' && <input style={{...inputStyle, width: '80px', padding: '0'}} type="color" value={item.labelColor === 'rgba(255,255,255,0.7)' ? '#ffffff' : item.labelColor} onChange={e => { const items = [...((block as any).cards)]; items[i].labelColor = e.target.value; updateBlock({ cards: items }); }} />}
                  
                  {block.type !== 'MiniCards' && <input style={{...inputStyle, width: '100px'}} placeholder="Icon/No" value={item.icon || item.number} onChange={e => { const items = [...((block as any).items)]; if(block.type==='Features') items[i].icon = e.target.value; else items[i].number = e.target.value; updateBlock({ items }); }} />}
                  
                  <input style={{...inputStyle, flex: 1}} placeholder="Title" value={item.title} onChange={e => { const items = [...((block as any).items || (block as any).cards)]; items[i].title = e.target.value; updateBlock({ [(block as any).items ? 'items' : 'cards']: items } as any); }} />
                  <input style={{...inputStyle, flex: 2}} placeholder="Description" value={item.description} onChange={e => { const items = [...((block as any).items || (block as any).cards)]; items[i].description = e.target.value; updateBlock({ [(block as any).items ? 'items' : 'cards']: items } as any); }} />
                  <button onClick={() => {
                     const items = [...((block as any).items || (block as any).cards)];
                     items.splice(i, 1);
                     updateBlock({ [(block as any).items ? 'items' : 'cards']: items } as any);
                  }} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#dc2626' }}>X</button>
                </div>
              ))}
              <button onClick={() => {
                const items = [...((block as any).items || (block as any).cards || [])];
                items.push(block.type === 'MiniCards' ? { label: 'New', labelColor: '#b80000', title: 'Title', description: 'Desc' } : { icon: '00', number: '00', title: 'Title', description: 'Desc' });
                updateBlock({ [(block as any).items ? 'items' : 'cards']: items } as any);
              }} style={{ padding: '8px 16px', borderRadius: '8px', border: '2px dashed var(--glass-border)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', fontWeight: 600, color: '#94a3b8', transition: 'all 0.2s', width: '100%' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--glass-border)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              >+ Thêm mục mới</button>
            </div>
          </div>
        )}

        {block.type === 'Stats' && (
          <div>
            <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Các con số thống kê</label></div>
            {block.items.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input style={{...inputStyle, flex: 1}} value={item.label} onChange={e => { const newItems = [...block.items]; newItems[idx].label = e.target.value; updateBlock({ items: newItems }); }} placeholder="Nhãn (VD: Khách hàng)" />
                <input style={{...inputStyle, flex: 1}} value={item.value} onChange={e => { const newItems = [...block.items]; newItems[idx].value = e.target.value; updateBlock({ items: newItems }); }} placeholder="Giá trị (VD: 500+)" />
                <button type="button" onClick={() => { const newItems = block.items.filter((_, i) => i !== idx); updateBlock({ items: newItems }); }} style={{ padding: '8px 16px', background: 'rgba(239, 68, 68, 0.2)', color: '#f87171', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Xóa</button>
              </div>
            ))}
            <button type="button" onClick={() => updateBlock({ items: [...block.items, { label: 'Nhãn mới', value: '0' }] })} style={uploadBtnStyle}>+ Thêm thống kê</button>
          </div>
        )}

        {block.type === 'CallToAction' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Tiêu đề chính</label><input style={inputStyle} value={block.title} onChange={e => updateBlock({ title: e.target.value })} /></div>
            <div><label style={labelStyle}>Tên nút (Button Text)</label><input style={inputStyle} value={block.buttonText} onChange={e => updateBlock({ buttonText: e.target.value })} /></div>
            <div><label style={labelStyle}>Link nút</label><input style={inputStyle} value={block.buttonLink} onChange={e => updateBlock({ buttonLink: e.target.value })} /></div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <FormSkeletonLoader />;

  return (
    <div>
      <div style={{ background: 'var(--glass-bg)', padding: '32px', borderRadius: '24px', border: '1px solid var(--glass-border)', boxShadow: '0 12px 24px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#f8fafc' }}>Xây dựng Trang chủ</h2>
            <p style={{ margin: '8px 0 0', color: '#94a3b8', fontSize: '15px' }}>Thêm, bớt và tùy chỉnh các khối nội dung hiển thị trên trang chủ.</p>
          </div>
          <button onClick={handleSave} disabled={saving} style={{ padding: '12px 28px', borderRadius: '12px', background: 'var(--neon-red)', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '15px', boxShadow: '0 0 10px rgba(239, 68, 68, 0.4)', transition: 'transform 0.1s' }}
            onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.96)')}
            onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
          >{saving ? 'Đang lưu...' : 'Lưu thay đổi'}</button>
        </div>

        <div style={{ marginBottom: '40px', display: 'flex', gap: '12px', flexWrap: 'wrap', padding: '16px', background: 'var(--glass-bg)', backdropFilter: 'blur(10px)', borderRadius: '16px', border: '1px solid var(--glass-border)', boxShadow: '0 8px 16px rgba(0,0,0,0.2)', position: 'sticky', top: '80px', zIndex: 100 }}>
          <span style={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: '#cbd5e1', marginRight: '8px' }}>Thêm khối:</span>
          <button onClick={() => addBlock('Hero')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Hero Banner</button>
          <button onClick={() => addBlock('SplitBanner')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Split Banner</button>
          <button onClick={() => addBlock('MiniCards')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Mini Cards</button>
          <button onClick={() => addBlock('ProductsList')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Products Grid</button>
          <button onClick={() => addBlock('Features')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Features</button>
          <button onClick={() => addBlock('Stats')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Stats</button>
          <button onClick={() => addBlock('LatestNews')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Latest News</button>
          <button onClick={() => addBlock('CallToAction')} style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#f1f5f9', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}>+ Call To Action</button>
        </div>

        <div>
          {blocks.length === 0 ? (
            <p style={{ color: '#94a3b8' }}>Trang chủ hiện đang trống. Hãy thêm các khối (blocks) ở trên.</p>
          ) : (
            blocks.map((block, index) => renderBlockEditor(block, index))
          )}
        </div>
      </div>

      {confirmDeleteIdx !== null && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setConfirmDeleteIdx(null)}>
          <div style={{
            background: 'var(--dark-bg)', border: '1px solid var(--glass-border)', borderRadius: '16px', padding: '32px',
            width: '90%', maxWidth: '400px', boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>Xác nhận xoá</h3>
              <button onClick={() => setConfirmDeleteIdx(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            <p style={{ color: '#cbd5e1', fontSize: '15px', lineHeight: 1.5, marginBottom: '32px' }}>
              Bạn có chắc chắn muốn xóa khối <strong style={{color: '#f8fafc'}}>{blocks[confirmDeleteIdx]?.type}</strong> này khỏi trang chủ?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setConfirmDeleteIdx(null)} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)',
                color: '#cbd5e1', border: 'none', fontWeight: 600, cursor: 'pointer',
              }}>Hủy</button>
              <button type="button" onClick={confirmRemoveBlock} style={{
                flex: 1, padding: '12px', borderRadius: '10px', background: '#dc2626',
                color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer',
              }}>Xác nhận xoá</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
